import type React from 'react';
import { MeshGradient } from '@paper-design/shaders-react';

type Phase = 'rest' | 'damage' | 'hold' | 'unwarp' | 'heal' | 'fade';

/** Natural fracture network radiating from an impact point on the visual-left
    upper shield (source x > 32 because the shield is drawn inside a scale(-1,1)
    mirror). Irregular segment lengths + tapering width (thick at impact, needle
    tips at the ends) so it reads as split metal, not a decorative zigzag.
    w0/w1 = width at start/end, in 64-unit shield space. */
export const SHIELD_PATH = "M32 4 C26 4 18 7 14 8.5 L14 28 C14 40 32 56 32 56 C32 56 50 40 50 28 L50 8.5 C46 7 38 4 32 4Z";
export const SWORD_PATH = "M101.938 19.938c-8.837 0-15.813 6.978-15.813 15.812 0 7.358 4.84 13.427 11.563 15.25l23.134 55.4c-12.618 7.364-23.065 19.85-30.17 37.528L64.22 163.75l19.395 8.803c31.722-21.112 72.4-39.987 110.12-49.975l27.327-4.078-2.78-18.5-33.294 4.982c-13.01-4.957-25.152-7.096-36.113-6.607-3.67.164-7.202.62-10.6 1.334l-23.088-55.304c1.6-2.48 2.532-5.436 2.532-8.656 0-8.837-6.945-15.813-15.782-15.813zM178.81 147c-17.714 6.11-35.908 13.923-52.875 22.656l68.407 165.03 14.625-7.842 28.28-38.75L178.812 147zm169.094 111.53l-68.125 3.033-56.967 78-68.438 37.781-28.47 117.188h345.25l-15-63.686-165.56-54.688 95.779 11.938-38.47-129.563z";
export const STAR_V = "M41 10 L42.5 14.5 L41 19 L39.5 14.5 Z";
export const STAR_H = "M36.5 14.5 L41 12.5 L45.5 14.5 L41 16.5 Z";

export interface Crack { pts: [number, number][]; w0: number; w1: number }
export const CRACKS: Crack[] = [
  // Primary splits
  { pts: [[38, 18], [36.2, 24], [37.4, 27.5], [35.1, 35], [35.8, 39], [33.6, 47], [34, 51], [32.6, 57]], w0: 2.6, w1: 0.5 },
  { pts: [[38, 18], [39.5, 13], [38.6, 9.5], [40.8, 4.5], [41.2, 1.5]], w0: 2.4, w1: 0.6 },
  { pts: [[38, 18], [43.5, 20.5], [46, 20], [52, 23.2]], w0: 2.0, w1: 0.5 },
  { pts: [[36.8, 25.5], [41, 29], [43.8, 29.8], [48.5, 36], [51, 38.5]], w0: 1.6, w1: 0.3 },
  // Secondary branches
  { pts: [[37.4, 27.5], [33, 30], [29.5, 29.4], [25.5, 31]], w0: 1.3, w1: 0.2 },
  { pts: [[35.1, 35], [30.8, 37.5], [28.6, 37.1]], w0: 1.1, w1: 0.2 },
  { pts: [[39.5, 13], [44, 10.5], [46.5, 11.2]], w0: 1.1, w1: 0.2 },
  { pts: [[33.6, 47], [38.5, 48.8], [40.4, 47.6]], w0: 0.9, w1: 0.2 },
  { pts: [[43.5, 20.5], [45, 15.2], [44.4, 13]], w0: 0.9, w1: 0.2 },
  // Hairlines
  { pts: [[38.6, 9.5], [35.5, 7], [34.2, 7.6]], w0: 0.7, w1: 0.15 },
  { pts: [[41, 29], [42, 33.5]], w0: 0.6, w1: 0.15 },
  { pts: [[46, 20], [48.2, 16.5]], w0: 0.6, w1: 0.15 },
  { pts: [[35.8, 39], [39.4, 41.2]], w0: 0.6, w1: 0.15 },
];

/** Outline a polyline as a filled polygon whose width tapers from w0 to w1. */
export function taperedPolygon({ pts, w0, w1 }: Crack, scale: number): string {
  const segLens = pts.slice(1).map((p, i) => Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]));
  const total = segLens.reduce((a, b) => a + b, 0);
  let run = 0;
  const left: string[] = [];
  const right: string[] = [];
  pts.forEach((p, i) => {
    if (i > 0) run += segLens[i - 1];
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(pts.length - 1, i + 1)];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const half = ((w0 + (w1 - w0) * (run / total)) * scale) / 2;
    left.push(`${(p[0] + nx * half).toFixed(2)} ${(p[1] + ny * half).toFixed(2)}`);
    right.unshift(`${(p[0] - nx * half).toFixed(2)} ${(p[1] - ny * half).toFixed(2)}`);
  });
  return `M${left.join(' L')} L${right.join(' L')} Z`;
}

interface PaladinLogoProps {
  size?: number;
  className?: string;
  shimmer?: boolean;
  phase?: Phase;
  meshColors?: [string, string, string, string, string, string];
  meshSpeed?: number;
  meshDistortion?: number;
  meshSwirl?: number;
  /** When true, thickens strokes so they survive a pixelate SVG filter. */
  pixelArtMode?: boolean;
}

export function PaladinLogo({ size = 64, className = '', shimmer = false, phase, meshColors, meshSpeed, meshDistortion, meshSwirl, pixelArtMode = false }: PaladinLogoProps) {
  const uid = `logo-${size}-${Math.random().toString(36).slice(2, 8)}`;
  const gradId = `grad-${uid}`;
  const maskId = `mask-${uid}`;
  const outlineMaskId = `outline-mask-${uid}`;
  const crackClipId = `crack-clip-${uid}`;
  const shimmerId = `shimmer-${uid}`;
  const bevelHighId = `bevel-hi-${uid}`;
  const bevelLoId = `bevel-lo-${uid}`;
  const engraveFilterId = `engrave-${uid}`;
  const innerShadowId = `inner-shadow-${uid}`;
  const specularId = `specular-${uid}`;
  const bandId = `band-${uid}`;
  const crackOnlyMaskId = `crack-only-mask-${uid}`;
  const edgeMaskId = `edge-mask-${uid}`;
  const glowMaskId = `glow-mask-${uid}`;
  const bevelBlurId = `bevel-blur-${uid}`;
  const interiorClipId = `interior-clip-${uid}`;
  const glowId = `glow-${uid}`;

  const shieldPath = SHIELD_PATH;

  const swordPath = SWORD_PATH;

  const isCracked = phase === 'damage' || phase === 'hold' || phase === 'unwarp';
  const isHealing = phase === 'heal';
  const isSmall = size <= 32;

  // Derive outline color from the actual interpolated mesh colors — always in sync
  const outlineColor = meshColors
    ? (() => {
        // Average the 6 mesh colors to get a representative outline tone
        let r = 0, g = 0, b = 0;
        for (const hex of meshColors) {
          const h = hex.replace('#', '');
          r += parseInt(h.slice(0, 2), 16);
          g += parseInt(h.slice(2, 4), 16);
          b += parseInt(h.slice(4, 6), 16);
        }
        r = Math.round(r / 6);
        g = Math.round(g / 6);
        b = Math.round(b / 6);
        // Lighten slightly so the outline reads as a bright edge, not a muddy mid-tone
        r = Math.min(255, r + 30);
        g = Math.min(255, g + 30);
        b = Math.min(255, b + 30);
        return `rgb(${r},${g},${b})`;
      })()
    : undefined;

  const crackScale = pixelArtMode ? 1.25 : 0.6;
  const crackCutouts = (keyPrefix: string) => phase && CRACKS.map((c, i) => (
    <path
      key={`${keyPrefix}-${i}`}
      fill="black"
      style={{
        d: `path("${taperedPolygon(c, isCracked ? crackScale : 0)}")`,
        transition: phase === 'damage' ? 'none' : 'd 2.8s ease-in-out',
      } as React.CSSProperties}
    />
  ));

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      style={{ overflow: 'visible' }}
    >
      <defs>
        {/* Base gradient (fallback when no mesh) */}
        <linearGradient id={gradId} x1="32" y1="4" x2="32" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#E8EDF2" />
          <stop offset="50%" stopColor="#9BAFC4" />
          <stop offset="100%" stopColor="#7B98B2" />
        </linearGradient>

        {/* Bevel highlight — light from top-left */}
        <linearGradient id={bevelHighId} x1="20" y1="4" x2="44" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="white" stopOpacity="0.5" />
          <stop offset="40%" stopColor="white" stopOpacity="0.08" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>

        {/* Bevel shadow — dark at bottom-right */}
        <linearGradient id={bevelLoId} x1="44" y1="56" x2="20" y2="4" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="black" stopOpacity="0.5" />
          <stop offset="35%" stopColor="black" stopOpacity="0.12" />
          <stop offset="100%" stopColor="black" stopOpacity="0" />
        </linearGradient>

        {/* Specular top-edge highlight — makes the rim gleam */}
        <linearGradient id={specularId} x1="32" y1="2" x2="32" y2="20" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="white" stopOpacity="0.6" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>

        {/* Arcade banding + back glow (pixel-art mode only) */}
        <linearGradient id={bandId} x1="0" y1="4" x2="0" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="white" stopOpacity="0.45" />
          <stop offset="0.14" stopColor="white" stopOpacity="0.3" />
          <stop offset="0.3" stopColor="white" stopOpacity="0" />
          <stop offset="0.55" stopColor="#021018" stopOpacity="0" />
          <stop offset="1" stopColor="#021018" stopOpacity="0.1" />
        </linearGradient>
        <clipPath id={interiorClipId}>
          <path d={shieldPath} />
        </clipPath>
        <filter id={bevelBlurId} x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="0.35" />
        </filter>
        <filter id={glowId} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>

        {/* Engraved cutout filter — inset shadow for depth */}
        <filter id={engraveFilterId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" result="blur" />
          <feOffset in="blur" dx="0.4" dy="0.6" result="offsetBlur" />
          <feFlood floodColor="black" floodOpacity="0.6" result="color" />
          <feComposite in="color" in2="offsetBlur" operator="in" result="shadow" />
          <feMerge>
            <feMergeNode in="shadow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Inner shadow filter for the shield body — adds depth to edges */}
        <filter id={innerShadowId} x="-10%" y="-10%" width="120%" height="120%">
          <feComponentTransfer in="SourceAlpha">
            <feFuncA type="table" tableValues="1 0" />
          </feComponentTransfer>
          <feGaussianBlur stdDeviation="1.5" />
          <feOffset dx="0" dy="1" result="innerShadow" />
          <feFlood floodColor="black" floodOpacity="0.3" />
          <feComposite in2="innerShadow" operator="in" />
          <feComposite in2="SourceGraphic" operator="over" />
        </filter>

        {/* Emblem + crack cutout mask — sword, stars, and sever lines are all
            transparent so the background (meteors, glows) shows through. */}
        <mask id={maskId}>
          <path d={shieldPath} fill="white" />
          <g transform="translate(8, 9) scale(0.096)">
            <path d={swordPath} fill="black" />
          </g>
          <path d={STAR_V} fill="black" />
          <path d={STAR_H} fill="black" />
          {crackCutouts('mc')}
        </mask>

        {/* Outline mask — only the cracks cut the rim, NOT the sword-in-stone emblem.
            So the shield border stays intact around the bottom stone (instead of being
            erased by the stone cutout), but cracks still slice through the rim. */}
        <mask id={outlineMaskId}>
          <path d={shieldPath} fill="white" />
          {crackCutouts('oc')}
        </mask>

        {/* Crack-only mask — unbounded, so outer outline/glow/3D edge extend past the rim */}
        <mask id={crackOnlyMaskId} maskUnits="userSpaceOnUse" x="-20" y="-20" width="104" height="104">
          <rect x="-20" y="-20" width="104" height="104" fill="white" />
          {crackCutouts('xc')}
        </mask>

        {/* 3D edge mask — only the sliver that sticks out below the rim, so the
            extrusion never fills the cracks or emblem cutouts */}
        <mask id={edgeMaskId} maskUnits="userSpaceOnUse" x="-20" y="-20" width="104" height="104">
          <rect x="-20" y="-20" width="104" height="104" fill="white" />
          <path d={shieldPath} fill="black" />
          {crackCutouts('ec')}
        </mask>

        {/* Glow mask — like crack-only, but the emblem is cut too so the glow never tints it */}
        <mask id={glowMaskId} maskUnits="userSpaceOnUse" x="-20" y="-20" width="104" height="104">
          <rect x="-20" y="-20" width="104" height="104" fill="white" />
          <g transform="translate(8, 9) scale(0.096)">
            <path d={swordPath} fill="black" />
          </g>
          <path d={STAR_V} fill="black" />
          <path d={STAR_H} fill="black" />
          {crackCutouts('gc')}
        </mask>

        {phase && (
          <clipPath id={crackClipId}>
            <path d={shieldPath} />
          </clipPath>
        )}

        {shimmer && (
          <linearGradient id={shimmerId} gradientUnits="userSpaceOnUse" x1="-10" y1="-10" x2="74" y2="74">
            <stop offset="0%" stopColor="white" stopOpacity="0">
              <animate attributeName="offset" values="-0.3;1.0" dur="8s" repeatCount="indefinite" />
            </stop>
            <stop offset="5%" stopColor="white" stopOpacity="0.3">
              <animate attributeName="offset" values="-0.2;1.1" dur="8s" repeatCount="indefinite" />
            </stop>
            <stop offset="10%" stopColor="white" stopOpacity="0">
              <animate attributeName="offset" values="-0.1;1.2" dur="8s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
        )}
      </defs>

      {/* Mirror entire shield horizontally */}
      <g transform="scale(-1,1) translate(-64,0)">

      {/* Shield body */}
      <g>
        {pixelArtMode && (
          <>
            <path
              d={shieldPath}
              fill={meshColors?.[1] ?? '#5EA0BC'}
              filter={`url(#${glowId})`}
              mask={`url(#${glowMaskId})`}
              style={{ opacity: 0.55 }}
            />
            {/* Stacked 3D edge */}
            <g mask={`url(#${edgeMaskId})`}>
              {[1.8, 1.2, 0.6].map(dy => (
                <path
                  key={dy}
                  d={shieldPath}
                  transform={`translate(0 ${dy})`}
                  fill={dy > 1 ? '#020A10' : '#062634'}
                  stroke="#020A10"
                  strokeWidth={2.6}
                  strokeLinejoin="miter"
                />
              ))}
            </g>
            {/* Dark outer outline */}
            <path
              d={shieldPath}
              stroke="#020A10"
              strokeWidth={3.6}
              fill="none"
              strokeLinejoin="miter"
              mask={`url(#${crackOnlyMaskId})`}
            />
          </>
        )}

        {/* Outline — uses outlineMaskId so ONLY the cracks cut through the rim.
            The sword-in-stone emblem stays as a fill-only cutout, preserving the
            border around the bottom where the stone sits. */}
        <path
          d={shieldPath}
          stroke={outlineColor ?? `url(#${gradId})`}
          strokeWidth={isSmall ? 2.5 : 1.8}
          fill="none"
          strokeLinejoin="round"
          mask={`url(#${outlineMaskId})`}
          style={outlineColor ? {
            transition: 'none',  // color already lerped frame-by-frame via meshColors
          } : undefined}
        />

        {/* Healing glow pulse on outline — uses the same outline-only mask */}
        {phase && (
          <path
            d={shieldPath}
            stroke="#50E6B4"
            strokeWidth={isSmall ? 4 : 3}
            fill="none"
            strokeLinejoin="round"
            mask={`url(#${outlineMaskId})`}
            style={{
              filter: 'blur(1.5px)',
              opacity: isHealing ? 0.5 : 0,
              transition: isHealing ? 'opacity 0.8s ease-in' : 'opacity 1s ease-out',
            }}
          />
        )}

        {/* Primary fill — mesh gradient when available, static gradient fallback */}
        {shimmer && meshColors ? (
          <foreignObject
            x="0" y="0" width="64" height="64"
            mask={`url(#${maskId})`}
          >
            <div style={{ width: 64, height: 64 }}>
              <MeshGradient
                width={64}
                height={64}
                colors={meshColors}
                speed={meshSpeed ?? 0.2}
                distortion={meshDistortion ?? 0.3}
                swirl={meshSwirl ?? 0.4}
                grainMixer={0}
                grainOverlay={0}
              />
            </div>
          </foreignObject>
        ) : (
          <path
            d={shieldPath}
            fill={`url(#${gradId})`}
            mask={`url(#${maskId})`}
          />
        )}

        {/* ── Enhancement #1: Inner bevel — forged metal edge ── */}
        {/* Highlight stroke (top-left light) */}
        <path
          d={shieldPath}
          stroke={`url(#${bevelHighId})`}
          strokeWidth={isSmall ? 1.2 : 0.8}
          fill="none"
          strokeLinejoin="round"
          style={{ opacity: (phase === 'damage' || phase === 'hold') ? 0.3 : 0.7 }}
        />
        {/* Shadow stroke (bottom-right depth) */}
        <path
          d={shieldPath}
          stroke={`url(#${bevelLoId})`}
          strokeWidth={isSmall ? 1.0 : 0.6}
          fill="none"
          strokeLinejoin="round"
          style={{ opacity: (phase === 'damage' || phase === 'hold') ? 0.2 : 0.5 }}
        />

        {/* ── Enhancement #2: Specular rim gleam along top edge ── */}
        <path
          d={shieldPath}
          fill={`url(#${specularId})`}
          mask={`url(#${maskId})`}
          style={{ opacity: 0.35, mixBlendMode: 'overlay' }}
        />

        {pixelArtMode && (
          <path d={shieldPath} fill={`url(#${bandId})`} mask={`url(#${maskId})`} />
        )}
        {pixelArtMode && (
          <g mask={`url(#${maskId})`} fill="none" strokeLinejoin="round" filter={`url(#${bevelBlurId})`}>
            {/* Uniform pressed-in ring around every cutout edge (sword, stone, star) */}
            <g stroke="#021018" opacity={0.45}>
              <g transform="translate(8, 9) scale(0.096)">
                <path d={swordPath} strokeWidth={1.5 / 0.096} />
              </g>
              <path d={STAR_V} strokeWidth={1.2} />
              <path d={STAR_H} strokeWidth={1.2} />
            </g>
          </g>
        )}
        {pixelArtMode && (
          <g mask={`url(#${crackOnlyMaskId})`}>
            {/* Same soft press on the rim's inner edge, so where the stone meets the
                border it fades like every other cutout edge instead of a hard line */}
            <g clipPath={`url(#${interiorClipId})`}>
              <path
                d={shieldPath}
                fill="none"
                stroke="#021018"
                strokeWidth={3}
                opacity={0.45}
                strokeLinejoin="round"
                filter={`url(#${bevelBlurId})`}
              />
            </g>
          </g>
        )}
        {pixelArtMode && (
          <g mask={`url(#${maskId})`} fill="none" strokeLinejoin="round" filter={`url(#${bevelBlurId})`}>
            {/* Faint lit rim, same on every edge */}
            <g transform="translate(0.3 0.4)" stroke="#E6FAFF" opacity={0.3}>
              <g transform="translate(8, 9) scale(0.096)">
                <path d={swordPath} strokeWidth={0.5 / 0.096} />
              </g>
              <path d={STAR_V} strokeWidth={0.4} />
              <path d={STAR_H} strokeWidth={0.4} />
            </g>
          </g>
        )}

        {/* ── Enhancement #3: Engraved star cutout shadows ── */}
        {!pixelArtMode && <g filter={!isSmall ? `url(#${engraveFilterId})` : undefined} style={{ opacity: 0.3 }}>
          <path d="M41 10 L42.5 14.5 L41 19 L39.5 14.5 Z" fill="rgba(0,0,0,0.45)" />
          <path d="M36.5 14.5 L41 12.5 L45.5 14.5 L41 16.5 Z" fill="rgba(0,0,0,0.45)" />
        </g>}
      </g>

      {/* SVG shimmer (nav logo — no mesh available) */}
      {shimmer && !meshColors && (
        <path
          d={shieldPath}
          fill={`url(#${shimmerId})`}
          mask={`url(#${maskId})`}
          style={{ mixBlendMode: 'overlay' }}
        />
      )}

      {/* Cracks are now rendered as mask cutouts above, so they're transparent
          and the background shows through them (same as the sword-in-stone). */}

      </g>
    </svg>
  );
}
